import { useRef, useState } from 'react';

import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Collapse,
    Stack,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useAnalytics } from 'src/AnalyticsContext';
import { AssistanceLevelOptions } from 'src/constants/SectionGgResolutionTypes';

function AssistanceLevelDescriptions() {
    const { palette, spacing } = useTheme();
    const { trackEvent } = useAnalytics();

    const bottomRef = useRef<HTMLDivElement | null>(null);

    const [showLevelDescriptions, setShowLevelDescriptions] = useState(false);
    const [expandedLevelDescription, setExpandedLevelDescription] = useState<string | false>(false);

    const handleLevelDescriptionsEntered = () => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    };

    const handleToggleLevelDescriptions = () => {
        const noun = !showLevelDescriptions ? 'Shown' : 'Hidden';
        trackEvent(`Assistance Level Descriptions ${noun}`);

        setShowLevelDescriptions((prev) => !prev);
    };

    const handleExpandLevelDescription = (optionId: string, expanded: boolean) => {
        const noun = expanded ? 'Expanded' : 'Collapsed';
        trackEvent(`Assistance Level Description ${noun}`, { optionId });
        setExpandedLevelDescription(expanded ? optionId : false);
    };

    return (
        <Box sx={{ mt: 2.5 }}>
            <Stack
                direction="row"
                sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
            >
                <Typography variant="body2" sx={{ color: 'primary.main' }}>
                    Level descriptions
                </Typography>
                <Box onClick={handleToggleLevelDescriptions}>
                    <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                        {showLevelDescriptions ? 'Hide' : 'Show'}
                    </Typography>
                </Box>
            </Stack>
            <Collapse in={showLevelDescriptions} onEntered={handleLevelDescriptionsEntered}>
                <Box sx={{ pt: 0.5 }}>
                    {AssistanceLevelOptions.map((option) => (
                        <Accordion
                            key={option.id}
                            expanded={expandedLevelDescription === option.id}
                            onChange={(_, expanded) =>
                                handleExpandLevelDescription(option.id, expanded)
                            }
                            disableGutters
                            sx={{
                                backgroundColor: 'transparent',
                                boxShadow: 'none',

                                '&:before': { display: 'none' },
                                '&:last-of-type': {
                                    borderBottom: 'none',
                                },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={
                                    <FontAwesomeIcon
                                        icon={
                                            expandedLevelDescription === option.id
                                                ? faMinus
                                                : faPlus
                                        }
                                        size="lg"
                                        color={palette.common.black}
                                    />
                                }
                                sx={{
                                    minHeight: spacing(4),
                                    px: 0,
                                    '& .MuiAccordionSummary-content': {
                                        my: 0,
                                    },
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {option.displayName}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0, pl: 0 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {option.description}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                    <div ref={bottomRef} />
                </Box>
            </Collapse>
        </Box>
    );
}

export default AssistanceLevelDescriptions;
